import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import '../styles/pages/AdminDashboard.css';

/**
 * AdminDashboard - 統合管理ダッシュボード
 * 管理者専用のシンプル認証（通常ログインとは別）
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ adminId: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ページタイトル設定
  useEffect(() => {
    document.title = '管理画面 | つむぎ';
  }, []);

  // 管理者認証状態を確認
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/api/admin/verify', {
          headers: { 'X-Admin-Token': token }
        });
        if (response.data.success) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('adminToken');
        }
      } catch (error) {
        localStorage.removeItem('adminToken');
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await api.post('/api/admin/login', {
        adminId: loginForm.adminId,
        password: loginForm.password
      });

      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setIsAuthenticated(true);
      }
    } catch (error) {
      setLoginError(error.response?.data?.error || 'ログインに失敗しました');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setLoginForm({ adminId: '', password: '' });
  };

  const adminMenuItems = [
    {
      id: 'announcements',
      title: 'お知らせ管理',
      description: 'マイページに表示するお知らせの作成・編集',
      icon: '📢',
      path: '/admin/announcements',
      color: '#7c3aed'
    },
    {
      id: 'affiliate',
      title: 'アフィリエイト商品管理',
      description: 'おすすめ商品の登録・編集、ウィジェット設定',
      icon: '🛒',
      path: '/admin/affiliate',
      color: '#4CAF50'
    },
    {
      id: 'empathy-feed',
      title: '共感フィード管理',
      description: 'ユーザー投稿の管理・モデレーション',
      icon: '💬',
      path: '/admin/empathy-feed',
      color: '#2196F3'
    }
  ];

  // ローディング中
  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard__container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            読み込み中...
          </div>
        </div>
      </div>
    );
  }

  // ログインフォーム
  if (!isAuthenticated) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard__container">
          <div className="admin-dashboard__login-box">
            <h1 style={{ fontSize: '24px', marginBottom: '8px', textAlign: 'center' }}>
              管理画面
            </h1>
            <p style={{ color: '#666', marginBottom: '24px', textAlign: 'center', fontSize: '14px' }}>
              管理者IDとパスワードを入力してください
            </p>

            <form onSubmit={handleLogin}>
              {loginError && (
                <div style={{
                  background: '#ffebee',
                  color: '#c62828',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {loginError}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                  管理者ID
                </label>
                <input
                  type="text"
                  value={loginForm.adminId}
                  onChange={(e) => setLoginForm({ ...loginForm, adminId: e.target.value })}
                  placeholder="管理者ID"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                  パスワード
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="パスワード"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      paddingRight: '48px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: loginLoading ? '#ccc' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loginLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {loginLoading ? 'ログイン中...' : 'ログイン'}
              </button>
            </form>

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ← トップページに戻る
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ダッシュボード
  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard__container">
        <header className="admin-dashboard__header">
          <div>
            <h1 className="admin-dashboard__title">管理ダッシュボード</h1>
            <p className="admin-dashboard__subtitle">
              システム管理機能へようこそ
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ログアウト
          </button>
        </header>

        <div className="admin-dashboard__grid">
          {adminMenuItems.map((item) => (
            <button
              key={item.id}
              className="admin-dashboard__card"
              onClick={() => navigate(item.path)}
              style={{ '--card-color': item.color }}
            >
              <div className="admin-dashboard__card-icon">{item.icon}</div>
              <h2 className="admin-dashboard__card-title">{item.title}</h2>
              <p className="admin-dashboard__card-description">
                {item.description}
              </p>
              <div className="admin-dashboard__card-arrow">→</div>
            </button>
          ))}
        </div>

        <div className="admin-dashboard__footer">
          <button
            onClick={() => navigate('/')}
            className="admin-dashboard__back-button"
          >
            ← トップページに戻る
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
